/**
 * Storage adapters.
 *
 * LocalStorageAdapter is the Phase One adapter. MemoryAdapter is used by tests
 * and by any environment where localStorage is missing or blocked (private
 * windows, SSR, jsdom without storage). Both are namespaced so several schema
 * versions can coexist and so nothing in the app collides with another app on
 * the same origin.
 */

import { namespacedKey, STORAGE_NAMESPACE, type StorageAdapter } from './types';

export class MemoryAdapter implements StorageAdapter {
  private readonly store = new Map<string, string>();

  constructor(private readonly namespace: string = STORAGE_NAMESPACE) {}

  async get<T>(key: string): Promise<T | null> {
    const raw = this.store.get(namespacedKey(this.namespace, key));
    if (raw === undefined) return null;
    return parse<T>(raw);
  }

  async set<T>(key: string, value: T): Promise<void> {
    this.store.set(namespacedKey(this.namespace, key), JSON.stringify(value));
  }

  async remove(key: string): Promise<void> {
    this.store.delete(namespacedKey(this.namespace, key));
  }

  async list(): Promise<string[]> {
    const prefix = `${this.namespace}:`;
    return [...this.store.keys()]
      .filter((k) => k.startsWith(prefix))
      .map((k) => k.slice(prefix.length))
      .sort();
  }
}

/**
 * localStorage is synchronous and can throw: quota exceeded, disabled storage,
 * a private window. Every access is guarded and a failure degrades to a no-op
 * rather than taking the app down. A write that fails is reported through
 * `onError` so the settings drawer can tell the player their progress is not
 * being saved.
 */
export class LocalStorageAdapter implements StorageAdapter {
  private readonly backing: Storage | null;

  constructor(
    private readonly namespace: string = STORAGE_NAMESPACE,
    private readonly onError: (op: string, key: string, error: unknown) => void = () => {},
    storage?: Storage,
  ) {
    this.backing = storage ?? safeLocalStorage();
  }

  /** False when storage is unavailable, so callers can warn the player. */
  get available(): boolean {
    return this.backing !== null;
  }

  async get<T>(key: string): Promise<T | null> {
    if (!this.backing) return null;
    try {
      const raw = this.backing.getItem(namespacedKey(this.namespace, key));
      if (raw === null) return null;
      return parse<T>(raw);
    } catch (error) {
      this.onError('get', key, error);
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    if (!this.backing) return;
    try {
      this.backing.setItem(namespacedKey(this.namespace, key), JSON.stringify(value));
    } catch (error) {
      this.onError('set', key, error);
    }
  }

  async remove(key: string): Promise<void> {
    if (!this.backing) return;
    try {
      this.backing.removeItem(namespacedKey(this.namespace, key));
    } catch (error) {
      this.onError('remove', key, error);
    }
  }

  async list(): Promise<string[]> {
    if (!this.backing) return [];
    try {
      const prefix = `${this.namespace}:`;
      const out: string[] = [];
      for (let i = 0; i < this.backing.length; i++) {
        const full = this.backing.key(i);
        if (full && full.startsWith(prefix)) out.push(full.slice(prefix.length));
      }
      return out.sort();
    } catch (error) {
      this.onError('list', '*', error);
      return [];
    }
  }
}

function parse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function safeLocalStorage(): Storage | null {
  try {
    if (typeof globalThis === 'undefined') return null;
    const ls = (globalThis as { localStorage?: Storage }).localStorage;
    if (!ls) return null;
    // Touch it: Safari in private mode throws only on use.
    const probe = `${STORAGE_NAMESPACE}:__probe`;
    ls.setItem(probe, '1');
    ls.removeItem(probe);
    return ls;
  } catch {
    return null;
  }
}

/** The adapter the app runs on. Tests swap it with `setDefaultAdapter`. */
let defaultAdapter: StorageAdapter = new LocalStorageAdapter();

export function getDefaultAdapter(): StorageAdapter {
  return defaultAdapter;
}

export function setDefaultAdapter(adapter: StorageAdapter): void {
  defaultAdapter = adapter;
}
